/**
 * Shared animation configuration constants
 */

/**
 * Keep a fade on Motion's own frameloop instead of a browser (WAAPI) animation.
 *
 * Motion 12.23 ends a WAAPI animation by setting the motion value — rendered on the NEXT frame —
 * and cancelling the browser animation at once, so for one frame the element shows its stale
 * inline style: an arriving page blinks back to opacity 0, a closing panel and its backdrop flash
 * back to full (the open / close / campaign-switch flicker). A non-zero `repeatDelay` is one of
 * Motion's WAAPI exclusions; with no `repeat` it changes nothing else. Transforms (`x`, `y`,
 * `scale`) already run on the frameloop, so the fade simply joins them. Spread it into every
 * opacity transition on a page-level surface. Drop it once Motion commits the final value before
 * cancelling.
 */
export const frameloopFade = { repeatDelay: 1e-6 } as const;

const baseTransition = {
  duration: 0.3,
  ease: "easeInOut",
  ...frameloopFade,
};

export const fadeInAnimation = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
  transition: {
    ...baseTransition,
  },
};

/** The panel's entrance spring: fast and settled — stiff enough to land in ~300ms, damped enough
 *  that nothing visibly overshoots (an overshoot on a clipped panel reads as a glitch). */
export const panelSpring = {
  type: "spring" as const,
  stiffness: 520,
  damping: 40,
  mass: 0.7,
};

/** Open sequence on phones: the campaign sheet rises first, the panel follows a beat behind it,
 *  then the content inside the panel — three fast steps that read as one fluid motion. */
const PANEL_DELAY = 0.05;
const CONTENT_DELAY = 0.11;

/** Widget open/close: the panel rises from below on `panelSpring` while fading in; no blur or
 *  scale (the blur filter was expensive and could stall mid-way). Exit is a quick drop + fade. */
export const widgetContainerAnimation = {
  initial: { opacity: 0, y: 28 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      y: { ...panelSpring, delay: PANEL_DELAY },
      opacity: {
        duration: 0.16,
        delay: PANEL_DELAY,
        ease: "easeOut" as const,
        ...frameloopFade,
      },
    },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.16,
      ease: [0.4, 0, 1, 1] as const,
      ...frameloopFade,
    },
  },
};

/** The panel's content on open and on every page change (a campaign switch): the leaving page
 *  scales down a touch and fades, the arriving page scales up into place and fades in, both on
 *  one fast, settled spring — a depth swap rather than a slide. The first page after the panel
 *  waits a beat (`custom={{ delay }}`); later pages arrive at once, overlapping the leaving one. */
export const pageSpring = {
  type: "spring" as const,
  stiffness: 600,
  damping: 40,
  mass: 0.6,
};
export const panelContentAnimation = {
  initial: { opacity: 0, scale: 0.97 },
  animate: ({ delay = 0 }: { delay?: number } = {}) => ({
    opacity: 1,
    scale: 1,
    transition: {
      scale: { ...pageSpring, delay },
      opacity: {
        duration: 0.16,
        delay,
        ease: "easeOut" as const,
        ...frameloopFade,
      },
    },
  }),
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: {
      duration: 0.14,
      ease: [0.4, 0, 1, 1] as const,
      ...frameloopFade,
    },
  },
};

/** The in-game campaign switch's scale-in: a touch stiffer and lighter than the panel spring so
 *  the new page snaps to size fast and settles without a visible overshoot. */
export const pageScaleSpring = {
  type: "spring" as const,
  stiffness: 600,
  damping: 38,
  mass: 0.6,
};

/** Delay the content waits behind the panel on the initial open (see the sequence above). */
export const PANEL_CONTENT_OPEN_DELAY = CONTENT_DELAY;

/** The side dock (phones held sideways): the same beat as the sheet below, rotated — in from
 *  the left edge. */
export const sideDockEnterAnimation = {
  initial: { x: -48 },
  animate: { x: 0, transition: panelSpring },
  exit: {
    x: -48,
    transition: { duration: 0.16, ease: [0.4, 0, 1, 1] as const },
  },
};

/** The in-game campaign sheet leads the open: it rises from the frame bottom with no delay, and
 *  drops back when the widget closes. */
export const dockEnterAnimation = {
  initial: { y: 48 },
  animate: { y: 0, transition: panelSpring },
  exit: {
    y: 48,
    transition: { duration: 0.16, ease: [0.4, 0, 1, 1] as const },
  },
};

/** The sheet's unfold: as quick as the desktop dropdown but without its bounce — the unfolding
 *  list is inside an overflow-hidden surface, and an overshoot on height would clip the last row.
 *  Stiff and well damped: it lands in about 150ms and stays put. */
export const sheetUnfoldSpring = {
  type: "spring" as const,
  stiffness: 720,
  damping: 42,
  mass: 0.6,
};

// Route transition animation configs
export const springConfig = {
  type: "spring" as const,
  stiffness: 500,
  damping: 35,
  mass: 0.5,
};

export const exitSpringConfig = {
  type: "spring" as const,
  stiffness: 550,
  damping: 38,
  mass: 0.45,
};

export const fadeTransition = {
  duration: 0.2,
} as const;

/** The in-game campaign switcher's signature motion — the dropdown's unfold and the selected
 *  campaign's pop-in share it on desktop, and the mobile dock reuses it so both surfaces feel
 *  like one control. */
export const switcherSpring = {
  type: "spring",
  stiffness: 300,
  damping: 15,
  mass: 0.5,
} as const;

/** The in-game cover springing between the full art and the header band's circle: 250ms, no
 *  bounce — a pop on a scroll-driven morph read as jiggle, and overshoot past the band's edge
 *  showed the clip. Duration-based (`visualDuration`): the move reads as done at 250ms. */
export const coverMorphSpring = {
  type: "spring" as const,
  visualDuration: 0.25,
  bounce: 0,
};
